import React, { useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  TradeOrder,
  VybePlacedType,
} from "@/vybe-trader-client/trade-history/types";
import PositionItem from "./PositionItem";
import { useSortPositions } from "@/vybe-trader-client/trade-history/hooks/useSortTrades";
import { useFilterPositions } from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";
import { useFilterStore } from "@/vybe-trader-client/trade-history/stores/useFilterStore";

interface PositionsListProps {
  onClick: (position: TradeOrder) => void;
}

export default function PositionsList({ onClick }: PositionsListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Get filters from store
  const selectedSymbol = useFilterStore((state) => state.selectedSymbol);
  const dateFilter = useFilterStore((state) => state.dateFilter);
  const customDateFrom = useFilterStore((state) => state.customDateFrom);
  const customDateTo = useFilterStore((state) => state.customDateTo);

  // Use the new filter hook
  const positions = useFilterPositions({
    symbol: selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
  });

  // Use sorting hook
  const sortedPositions = useSortPositions(positions);

  const virtualizer = useVirtualizer({
    count: sortedPositions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52, // Height of PositionItem
    overscan: 5,
  });

  // Force virtualizer to update when positions change
  useEffect(() => {
    virtualizer.measure();
  }, [sortedPositions.length, virtualizer]);

  const items = virtualizer.getVirtualItems();

  // Function to get the border color based on placed type
  const getBorderColor = (placedType: number) => {
    if (placedType === VybePlacedType.OnSL) {
      return "border-red-500"; // Stop Loss - red
    } else if (placedType === VybePlacedType.OnTP) {
      return "border-green-500"; // Take Profit - green
    }
    return "";
  };

  return (
    <div ref={parentRef} className="h-full overflow-auto">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {items.map((virtualRow) => {
          const position = sortedPositions[virtualRow.index];
          const borderClass = getBorderColor(position.placedType);

          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className={`${borderClass ? `border-l-4 ${borderClass}` : ""}`}
            >
              <PositionItem
                position={position}
                onClick={() => onClick?.(position)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
